import {inject, Type} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';

import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

/** Minimal auth API used by {@link createAuthGuard}. Each app supplies its own `AuthService`. */
export interface AuthGuardAuth {
  isAuthenticated(): boolean;
  isAccessTokenExpired(): boolean;
  executeRefreshToken(): Observable<unknown>;
  scheduleRefreshToken(): void;
}

export interface CreateAuthGuardOptions {
  /** Absolute navigation commands for the login route (default `['/login']`). */
  loginPathSegments?: string[];
  /** Query param name for the attempted URL (default `returnUrl`). */
  returnUrlQueryParam?: string;
}

/**
 * Only allows same-app internal paths (open-redirect safe) for post-login navigation.
 */
export function safeInternalReturnUrl(
  raw: string | null | undefined,
  fallback: string,
): string {
  if (raw === null || raw === undefined || typeof raw !== 'string') {
    return fallback;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return fallback;
  }
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  if (trimmed.includes('://') || trimmed.includes('\\')) {
    return fallback;
  }
  return trimmed;
}

export function createAuthGuard(
  authServiceToken: Type<AuthGuardAuth>,
  options?: CreateAuthGuardOptions,
): CanActivateFn {
  const loginPathSegments = options?.loginPathSegments ?? ['/login'];
  const returnUrlQueryParam = options?.returnUrlQueryParam ?? 'returnUrl';

  return (_route, state) => {
    const authService = inject(authServiceToken);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      console.error('Access token expired or user is not authenticated!');

      if (authService.isAccessTokenExpired()) {
        console.error('Access token expired!');
        return authService.executeRefreshToken().pipe(
          map(() => {
            console.log('Access token refreshed successfully!');
            return true;
          }),
          catchError(err => {
            console.error('Error refreshing access token:', err);
            console.log('Redirecting to login...');
            const returnUrl = state.url || '/';
            return of(
              router.createUrlTree(loginPathSegments, {
                queryParams: {[returnUrlQueryParam]: returnUrl},
              }),
            );
          }),
        );
      } else {
        authService.scheduleRefreshToken();
        return true;
      }
    }
    return true;
  };
}
